import { Label } from "@tellescope/design-system";
import { Button, cn } from "@tellescope/ui";
import { TellescopeFormFieldBaseProps } from "../../types";
import { RelatedContact } from "./RelatedContact";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useStateWithFallback } from "@tellescope/react-hooks";
import { AddRelatedContactForm } from "./AddRelatedContactForm";
import { convertContactToRelatedContact } from "./convert-contact-to-related-contact";
import { convertRelatedContactToContact } from "./convert-related-contact-to-contact";
import { FieldDescription } from "../../FieldDescription";

export interface RelatedContactsFieldProps extends TellescopeFormFieldBaseProps<"Related Contacts"> {
  enduserId: string;
}

export const RelatedContactsField = ({
  title,
  description,
  htmlDescription,
  value,
  onChange,
  className,
  enduserId,
  onBlur,
  error,
  required,
  options,
}: RelatedContactsFieldProps) => {
  const { t } = useTranslation("forms");
  const [isAddingContact, setIsAddingContact] = useStateWithFallback<boolean>({ fallbackValue: !value?.length });
  const [indexBeingEdited, setIndexBeingEdited] = useState<number>();

  const contactEntryBeingEdited = indexBeingEdited === undefined ? undefined : value?.[indexBeingEdited];
  const contactBeingEdited = contactEntryBeingEdited
    ? convertContactToRelatedContact(contactEntryBeingEdited, enduserId)
    : undefined;

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {!!title && <Label required={required}>{title}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      {value?.map((contact, i) => {
        const convertedContact = convertContactToRelatedContact(contact, enduserId);
        if (!convertedContact) return null;
        if (i === indexBeingEdited) return null;
        return (
          <RelatedContact
            key={`${contact.fname}-${contact.email}-${contact.dateOfBirth ?? ""}-${i}`}
            contact={convertedContact}
            number={i + 1}
            onEdit={() => {
              setIndexBeingEdited(i);
            }}
            onDelete={() => {
              const newValue = value?.filter((_, existingIndex) => existingIndex !== i);

              onChange(newValue);
              if (!newValue?.length) {
                setIsAddingContact(true);
              }
            }}
          />
        );
      })}
      {isAddingContact || contactBeingEdited ? (
        <AddRelatedContactForm
          key={indexBeingEdited ?? "new"}
          initialContact={contactBeingEdited}
          onSave={(contact) => {
            const updatedContact = convertRelatedContactToContact(contact);
            onChange(
              indexBeingEdited === undefined
                ? [...(value ?? []), updatedContact]
                : (value ?? []).map((existingContact, existingIndex) =>
                    existingIndex === indexBeingEdited ? updatedContact : existingContact
                  )
            );
            setIsAddingContact(false);
            setIndexBeingEdited(undefined);
          }}
          onCancel={
            value?.length
              ? () => {
                  setIsAddingContact(false);
                  setIndexBeingEdited(undefined);
                }
              : undefined
          }
          onBlur={onBlur}
          hasSavedContact={!!value?.length}
          relatedContactTypes={options?.relatedContactTypes}
          hiddenDefaultFields={options?.hiddenDefaultFields}
          tableChoices={options?.tableChoices}
        />
      ) : (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setIsAddingContact(true)}>
            {t("Add Contact")}
          </Button>
        </div>
      )}
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
